import { html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { SlotController } from '../../controllers/slot-controller';
import { OutlineCard } from '../../base/outline-card/outline-card';
import '../ghs-icon/ghs-icon';

import componentStyles from './ghs-icon-card.css.lit';

/**
 * The Icon Card component.
 * @element ghs-card
 * @slot icon - The icon slot.
 * @slot image - The image slot.
 * @slot eyebrow - The eyebrow slot.
 * @slot heading - The heading slot.
 * @slot subheading - The subheading slot.
 * @slot content - The primary slot to render the card content.
 * @slot cta - The cta slot.
 */
@customElement('ghs-icon-card')
export class GhsIconCard extends OutlineCard {
  static styles = [componentStyles];

  slots = new SlotController(this, true);

  /**
   * Whether or not the component should be 100% height of its parent.
   * If TRUE, the Host will be `height: 100%`.
   */
  @property({ type: Boolean, attribute: 'height-grow', reflect: true })
  heightGrow = false;

  @state() hasIconSlot: boolean;
  @state() hasImgSlot: boolean;
  @state() hasEyebrowSlot: boolean;
  @state() hasHeadingSlot: boolean;
  @state() hasSubHeading: boolean;
  @state() hasContentSlot: boolean;
  @state() hasCtaSlot: boolean;

  /**
   * Sideways card variant.
   */
  @property({ type: Boolean, attribute: 'sideways', reflect: true })
  sideways: false;

  /**
   * Background color of the card. Defaults to 'white'
   */
  @property({ type: String, attribute: 'bg-color', reflect: true })
  bgColor: 'blue' | 'green' | 'white' | 'sky-blue' | 'none' = 'white';

  firstUpdated(): void {
    super.firstUpdated();
    this.hasIconSlot = this.slots.test('icon');
    this.hasImgSlot = this.slots.test('image');
    this.hasEyebrowSlot = this.slots.test('eyebrow');
    this.hasHeadingSlot = this.slots.test('heading');
    this.hasSubHeading = this.slots.test('subheading');
    this.hasContentSlot = this.slots.test('content');
    this.hasCtaSlot = this.slots.test('cta');
  }

  render(): TemplateResult {
    const wrapperClasses = {
      'card-wrapper': true,
      'sideways': this.sideways,
      'bg-color': this.bgColor,
    };

    return html`
      <div class="${classMap(wrapperClasses)}">
        <div class="card">
          ${this.hasIconSlot
            ? html`<div class="card-icon">
                <slot name="icon"></slot>
              </div> `
            : nothing}
          ${this.hasImgSlot
            ? html`<div class="card-image">
                <slot name="image"></slot>
              </div> `
            : nothing}
          <div class="card-body-wrapper">
            ${this.hasEyebrowSlot
              ? html`<div class="card-eyebrow">
                  <slot name="eyebrow"></slot>
                </div>`
              : nothing}
            ${this.hasHeadingSlot
              ? html`
                  <div class="card-heading">
                    <slot name="heading"></slot>
                  </div>
                `
              : nothing}
            ${this.hasSubHeading
              ? html`
                  <div class="card-subheading">
                    <slot name="subheading"></slot>
                  </div>
                `
              : nothing}
            ${this.hasContentSlot
              ? html`
                  <div class="card-content">
                    <slot name="content"></slot>
                  </div>
                `
              : ''}
            ${this.hasCtaSlot
              ? html`
                  <div class="card-cta">
                    <slot name="cta"></slot>
                  </div>
                `
              : ''}
          </div>
        </div>
      </div>
    `;
  }
}
